import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cond from 'lodash/cond';
import { SuiNotificationGroup, SuiSpinner } from '@splashthat/sui';
import { useDebouncedAnnounce } from 'common/compounds/LiveAnnouncer/LiveAnnouncer';
import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import { pluralizeWord } from 'common/utils/pluralizeWord';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import Block from 'common/components/TouchpointEditor/components/TouchpointNodes/Block';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { StyledSessionsListBlock } from '../Block.styles';

export default function SessionsListBlock(props) {
    const sessions = useSessions();
    const { theme } = useTouchpointData();
    const [notificationDismissed, setNotificationDismissed] = useState(false);

    const hasNoResults = sessions.data.length > 0 && sessions.filteredData.length === 0;

    const { searchTerm = '', tags = [], dates = [], startTimes = [], locations = [] } = sessions.filters || {};
    const hasActiveFilters = !!(searchTerm || tags.length || dates.length || startTimes.length || locations.length);

    const getAnnouncementMessage = () => {
        if (sessions.isLoading) return '';
        if (!sessions.data.length) return props.meta.emptyStateText;
        if (hasNoResults && hasActiveFilters) {
            return searchTerm ? `${props.meta.noResultsText} for ${searchTerm}` : props.meta.noResultsText;
        }
        if (sessions.filteredData.length > 0) {
            const count = sessions.filteredData.length;
            return `${count} ${pluralizeWord('session', count)}`;
        }
        return '';
    };

    useDebouncedAnnounce(getAnnouncementMessage(), 1500);

    const isEmptyState = () => !sessions.data.length;
    const isNoResultsState = () => hasNoResults;
    const isLoadingState = () => sessions.isLoading;

    const activeNotifications = sessions.isError
        ? [
              {
                  message: 'There has been an error retrieving the list of sessions.',
                  variation: 'ERROR',
                  onClose: () => setNotificationDismissed(true),
              },
          ]
        : [];

    return (
        <StyledSessionsListBlock
            {...props}
            $fontColor={theme.colors[props.styles.fontColor]}
            $fontFamily={theme.activeFonts[props.styles.fontFamily]}
            $fontSize={props.styles.fontSize}
        >
            {props.children}
            <SuiNotificationGroup notifications={notificationDismissed ? [] : activeNotifications} />
            {cond([
                [isLoadingState, () => <SuiSpinner loading size="large" aria-label="loading" />],
                [isEmptyState, () => props.meta.emptyStateText],
                [isNoResultsState, () => <span aria-hidden="true">{props.meta.noResultsText}</span>],
            ])()}
        </StyledSessionsListBlock>
    );
}

SessionsListBlock.splash = {
    ...Block.splash,
    related: {
        ...Block.splash.related,
        settings: React.lazy(() => import('./SessionsListBlock.tools')),
    },
    defaultProps: {
        ...Block.splash.defaultProps,
        styles: {
            ...Block.splash.defaultProps.styles,
            backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontFamily: BRAND_LIBRARY_BODY_TEXT,
            fontSize: 16,
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
        },
        meta: {
            ...Block.splash.defaultProps.meta,
            emptyStateText: 'There are no scheduled sessions. Check back for updates.',
            noResultsText: 'We couldn’t find any results.',
        },
    },
};

SessionsListBlock.propTypes = {
    children: PropTypes.node,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
    }).isRequired,
    meta: PropTypes.shape({
        emptyStateText: PropTypes.string,
        noResultsText: PropTypes.string,
        emptyMySessionsText: PropTypes.string,
    }).isRequired,
};

SessionsListBlock.defaultProps = {
    children: null,
};
